import { useEffect, useMemo, useState } from 'react';
import { outlierMessage } from '../outlier.js';
import { api } from '../api.js';
import { effectiveParam } from '../params.js';
import { ParamHistoryChart, filterByRange } from './ParamHistoryChart.jsx';
import { DoseCalculator } from './DoseCalculator.jsx';
import { relativeTimeShort, currentReadingsClient } from '../utils.js';
import { testTimeUnknown } from '../../../shared/trident-trust.js';
import { isGradedParam } from '../../../shared/tank-purpose.js';
import { getDisplayUnits } from '../units.js';
import { readSalinityUnit, sgDecimals } from '../salinity.js';
import {
  historyStatsForRange,
  makeHistoryFormat,
  historyChartParam,
  readingsToDisplay,
} from '../param-history.js';
import { useConfirm } from './ConfirmDialog.jsx';

// Full-screen reading-history view. Opens when the user taps the big
// value on a Home param card. Shows:
//   - Stats strip: latest value, 24h delta, in-band %, reading count
//   - Live trend chart (gradient-filled, pulsing latest-reading dot)
//   - Range selector down to 24 h
//   - Reading list with source badges + delete
//   - Polls /api/tanks/:id every 30 s while open so probe-fed tanks
//     see the chart move in close to real time
//
// Replaces the old modal sheet — too cramped on phones for a chart
// + range buttons + readings list to all share one viewport.

const DOSABLE_PARAMS = new Set(['alk', 'calcium', 'magnesium']);

const RANGE_OPTIONS = [
  { k: 1, label: '24h' },
  { k: 7, label: '7d' },
  { k: 30, label: '30d' },
  { k: 90, label: '90d' },
  { k: 365, label: '1y' },
  { k: 0, label: 'All' },
];

const SOURCE_LABEL = {
  apex: 'APEX',
  hydros: 'HYDROS',
  manual: 'MANUAL',
  hanna: 'HANNA',
  strip: 'STRIP',
  refract: 'REFRACT',
  icp: 'ICP',
  import: 'IMPORTED',
  computed: 'COMPUTED',
};

export function ParamHistorySheet({ tank, paramKey, readings, onClose, onReload }) {
  const [deleting, setDeleting] = useState(null);
  const [err, setErr] = useState('');
  const [rangeDays, setRangeDays] = useState(7);
  const [doseOpen, setDoseOpen] = useState(false);
  const [lastTick, setLastTick] = useState(Date.now());
  const [fullHistory, setFullHistory] = useState(null);
  const p = effectiveParam(tank, paramKey);
  const confirm = useConfirm();
  // Every number on this page in the keeper's units (°C, their salinity
  // scale), the way Home and All history show them.
  // An S.G. band printed at four places gets its values, range, 24h change
  // and axis at four too: at three, 1.0264 read as 1.026 inside 1.0258-1.0262.
  // The places also follow the newest reading the way its Home card does
  // (1.0272 S.G. HIGH beside a band ending 1.027), and each value gets the
  // card's own rule through sgBand.
  const bandLo = p?.acceptable?.[0];
  const bandHi = p?.acceptable?.[1];
  const newestValue = readings?.[0]?.value;
  const opts = useMemo(() => {
    const salinityUnit = readSalinityUnit();
    return {
      salinityUnit,
      tempUnit: getDisplayUnits(tank).temp,
      ...(paramKey === 'salinity' && salinityUnit === 'sg'
        ? {
            sgDecimals: sgDecimals([bandLo, bandHi], newestValue),
            sgBand: [bandLo, bandHi],
          }
        : {}),
    };
  }, [tank, paramKey, bandLo, bandHi, newestValue]);
  const fmt = useMemo(() => makeHistoryFormat(paramKey, p?.unit, opts), [paramKey, p, opts]);

  // The tank payload only carries the latest ~200 rows per param — a
  // probe at 15-min cadence burns that in days, which silently capped
  // this view at about a week regardless of the range picked
  // (user-reported on temperature). Fetch the REAL window ourselves;
  // the prop readings render instantly while this loads.
  useEffect(() => {
    let alive = true;
    // At least two days, so the 24h range still holds the reading the
    // 24h change compares against.
    const days = rangeDays === 0 ? null : Math.max(2, Math.ceil(rangeDays));
    const qs = `param=${encodeURIComponent(paramKey)}${days ? `&days=${days}` : ''}&limit=5000`;
    api(`/api/tanks/${tank.id}/readings?${qs}`)
      .then((rows) => {
        if (alive && Array.isArray(rows)) setFullHistory(rows);
      })
      .catch(() => {
        /* keep whatever we have — prop readings still render */
      });
    return () => {
      alive = false;
    };
  }, [tank.id, paramKey, rangeDays, lastTick]);

  const source = fullHistory || readings || [];
  // The row that speaks for the tank now, as Home and the server's dose
  // baseline read it: the keeper's own counting test before a Trident
  // number with no test time (shared/trident-trust.js). From the tank
  // payload, which always carries that test; the sheet's own fetch stops
  // at the chosen range.
  const currentRow = useMemo(
    () => currentReadingsClient(readings?.length ? readings : source, paramKey)[0] || null,
    [readings, source, paramKey]
  );
  const nowIsOwnTest = !!currentRow && source[0] && currentRow.id !== source[0].id;
  const nowTridentTime = !!currentRow && testTimeUnknown(currentRow, paramKey);

  // Live polling: refresh the tank every 30 s while open so probe-
  // fed param cards animate as new readings land. Stops on close to
  // avoid the cron continuing after the user goes back.
  useEffect(() => {
    const id = setInterval(() => {
      onReload()
        .then(() => setLastTick(Date.now()))
        .catch(() => {
          /* swallow — next tick retries */
        });
    }, 30_000);
    return () => clearInterval(id);
  }, [onReload]);

  // Esc closes the view — full-screen demands a keyboard exit.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => filterByRange(source, rangeDays), [source, rangeDays]);
  const stats = useMemo(() => historyStatsForRange(filtered, source, p), [filtered, source, p]);
  const chartReadings = useMemo(
    () => readingsToDisplay(paramKey, source, opts),
    [paramKey, source, opts]
  );
  const chartParam = useMemo(() => historyChartParam(paramKey, p, opts), [paramKey, p, opts]);

  // Off-band status drives the chart line colour — see
  // ParamHistoryChart's anti-pattern note.
  const status = useMemo(() => {
    if (!p || source.length === 0) return 'ok';
    if (!isGradedParam(tank, paramKey)) return 'ok';
    const latest = currentRow || source[0];
    if (!latest || testTimeUnknown(latest, paramKey)) return 'ok';
    if (!Array.isArray(p.acceptable) || p.acceptable.length !== 2) return 'ok';
    const [lo, hi] = p.acceptable.map(Number);
    const v = Number(latest.value);
    if (!Number.isFinite(v) || !Number.isFinite(lo) || !Number.isFinite(hi)) return 'ok';
    return v < lo || v > hi ? 'off' : 'ok';
  }, [p, source, tank, paramKey, currentRow]);

  // Readings viewed BY DATE: every row in the selected range, grouped
  // into days. Dense probe days (> 8 rows) start collapsed behind a
  // count + min–max summary; sparse days start open. `toggledDays`
  // remembers the keeper's clicks across the 30 s live re-renders.
  const [toggledDays, setToggledDays] = useState(() => new Set());
  const dayGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    for (const r of filtered) {
      const d = new Date(r.ts);
      const key = isNaN(d) ? 'unknown' : d.toDateString();
      if (!cur || cur.key !== key) {
        cur = { key, date: d, rows: [] };
        groups.push(cur);
      }
      cur.rows.push(r);
    }
    return groups;
  }, [filtered]);
  const toggleDay = (key) =>
    setToggledDays((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!p) return null;

  // No dose offer on a param the tank only logs (a cycling tank's alk, reef
  // chemistry in a QT), as on the Home card.
  const isDosable = DOSABLE_PARAMS.has(paramKey) && isGradedParam(tank, paramKey);
  // A viewer cannot delete; the button would only answer 403.
  const canDelete = tank.role !== 'viewer';

  // One wet-thumb tap on × used to remove a reading for good.
  const deleteRow = async (r) => {
    const id = r.id;
    const when = new Date(r.ts);
    const whenText = isNaN(when)
      ? ''
      : ` from ${when.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, ${when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    const ok = await confirm({
      title: `Delete ${fmt.value(r.value)}${fmt.unit ? ` ${fmt.unit}` : ''}${whenText}?`,
      body: `This removes the ${p.label.toLowerCase()} reading from your history. It cannot be undone.`,
      confirmLabel: 'Delete reading',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(id);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/readings/${id}`, { method: 'DELETE' });
      // Drop the row from the fetched window too — source prefers
      // fullHistory, so without this the deleted reading stayed on
      // screen until the next 30 s poll (review finding).
      setFullHistory((h) => (h ? h.filter((r) => r.id !== id) : h));
      await onReload();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    } finally {
      setDeleting(null);
    }
  };

  // Rows live under a day header now, so each row shows time only.
  const fmtRowTime = (ts) => {
    const d = new Date(ts);
    if (isNaN(d)) return '';
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const fmtDayLabel = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return 'Unknown date';
    const today = new Date();
    const yest = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yest.toDateString()) return 'Yesterday';
    const opts = { weekday: 'short', month: 'short', day: 'numeric' };
    if (date.getFullYear() !== today.getFullYear()) opts.year = 'numeric';
    return date.toLocaleDateString('en-US', opts);
  };

  // A 24 h change between Trident rows says nothing about the hand test
  // shown as Now.
  const deltaPretty =
    stats.delta24h == null || nowIsOwnTest ? null : fmt.delta(stats.prior24h, stats.last);

  return (
    <div className="ph-screen" role="dialog" aria-modal="true" aria-labelledby="ph-title">
      <div className="ph-screen-inner">
        <header className="ph-screen-head">
          <button type="button" className="ph-back" onClick={onClose} aria-label="Back to home">
            ←
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ph-eyebrow">Parameter history</div>
            <h2 id="ph-title" className="font-heading ph-title">
              {p.label.toUpperCase()}
            </h2>
          </div>
          <div className="ph-live">
            <span className="ph-live-dot" aria-hidden="true" />
            Live · updated {relativeTimeShort(lastTick)}
          </div>
        </header>

        {err && (
          <div role="alert" className="ph-err">
            {err}
          </div>
        )}

        <div className="ph-stats">
          <div className="ph-stat">
            <div className="ph-stat-label">Now</div>
            <div className="ph-stat-value">
              {fmt.value(currentRow ? currentRow.value : stats.last)}
              <span className="ph-stat-unit">{fmt.unit}</span>
            </div>
            {nowTridentTime && <div className="ph-stat-label">Trident, test time unknown</div>}
            {nowIsOwnTest && <div className="ph-stat-label">Your own test</div>}
          </div>
          <div className="ph-stat">
            <div className="ph-stat-label">24h change</div>
            <div
              className="ph-stat-value"
              style={{ color: stats.delta24h == null ? 'var(--text-dim)' : 'var(--text)' }}
            >
              {deltaPretty || '—'}
            </div>
          </div>
          <div className="ph-stat">
            <div className="ph-stat-label">Range</div>
            <div className="ph-stat-value">
              {stats.min == null ? '—' : `${fmt.value(stats.min)} – ${fmt.value(stats.max)}`}
            </div>
          </div>
          <div className="ph-stat">
            <div className="ph-stat-label">In band</div>
            <div className="ph-stat-value">
              {stats.inBandPct == null ? '—' : `${stats.inBandPct}%`}
            </div>
          </div>
        </div>

        <div className="ph-range" role="toolbar" aria-label="Time range">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              className={`ph-range-btn ${rangeDays === opt.k ? 'active' : ''}`}
              onClick={() => setRangeDays(opt.k)}
              aria-pressed={rangeDays === opt.k}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="ph-chart-wrap">
          <ParamHistoryChart
            readings={chartReadings}
            param={chartParam}
            status={status}
            rangeDays={rangeDays}
          />
        </div>

        {/* A viewer's preview answers 403, which the calculator words as
            "Check your entries" (review, 2026-10-23). */}
        {isDosable && tank.role !== 'viewer' && (
          <button type="button" className="ph-dose-btn" onClick={() => setDoseOpen(true)}>
            <span aria-hidden="true">⚗</span> Calculate {p.label.toLowerCase()} dose
          </button>
        )}

        {doseOpen && (
          <DoseCalculator
            tank={tank}
            param={paramKey}
            latestValue={currentRow?.value ?? null}
            onClose={() => setDoseOpen(false)}
            onReload={onReload}
          />
        )}

        <h3 className="ph-list-title">
          Readings
          {filtered.length > 0 && (
            <span
              style={{ fontSize: 13, color: 'var(--text-dim)', fontWeight: 400, marginLeft: 8 }}
            >
              {filtered.length} in this range
            </span>
          )}
        </h3>
        {filtered.length === 0 ? (
          <div className="ph-empty">
            {/* "Any data ever" must consider the prop readings too — an
                empty fetched WINDOW (e.g. default 7d on a tank last
                tested 10 days ago) is not "no readings yet" (review
                finding: the sheet claimed a data-less tank). */}
            {source.length === 0 && (readings || []).length === 0
              ? 'No readings yet. Tap + on the card to log your first one.'
              : 'Nothing in this range — pick a longer one above.'}
          </div>
        ) : (
          dayGroups.map((g) => {
            const defaultOpen = g.rows.length <= 8;
            const open = toggledDays.has(g.key) ? !defaultOpen : defaultOpen;
            const vals = g.rows.map((r) => Number(r.value)).filter(Number.isFinite);
            const lo = vals.length ? Math.min(...vals) : null;
            const hi = vals.length ? Math.max(...vals) : null;
            return (
              <div key={g.key} className="ph-day-group">
                <button
                  type="button"
                  className="ph-day-head font-heading"
                  onClick={() => toggleDay(g.key)}
                  aria-expanded={open}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 10,
                    padding: '10px 2px 6px',
                    color: 'var(--text-muted)',
                    fontSize: 13,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid var(--surface-border)',
                  }}
                >
                  <span aria-hidden="true" style={{ color: 'var(--text-dim)' }}>
                    {open ? '▾' : '▸'}
                  </span>
                  <span style={{ color: 'var(--text)' }}>{fmtDayLabel(g.date)}</span>
                  <span
                    className="font-mono"
                    style={{
                      marginLeft: 'auto',
                      fontSize: 12,
                      letterSpacing: 0,
                      textTransform: 'none',
                      color: 'var(--text-dim)',
                    }}
                  >
                    {g.rows.length} reading{g.rows.length === 1 ? '' : 's'}
                    {!open &&
                      lo != null &&
                      ` · ${fmt.value(lo)}–${fmt.value(hi)}${fmt.unit ? ` ${fmt.unit}` : ''}`}
                  </span>
                </button>
                {open && (
                  <ul className="ph-list">
                    {g.rows.map((r) => (
                      <li key={r.id} className="ph-row">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="font-mono"
                            style={{
                              fontSize: 18,
                              fontWeight: 500,
                              color: 'var(--text)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span>
                              {fmt.value(r.value)}
                              <span
                                style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 4 }}
                              >
                                {fmt.unit}
                              </span>
                            </span>
                            {r.method && (
                              <span className={`ph-source ph-source-${r.method}`}>
                                {SOURCE_LABEL[r.method] || r.method}
                              </span>
                            )}
                            {r.outlier_reason && (
                              <span
                                className="font-heading"
                                style={{
                                  fontSize: 11,
                                  letterSpacing: '0.18em',
                                  textTransform: 'uppercase',
                                  color: 'var(--alert)',
                                  border: '1px solid var(--alert)',
                                  borderRadius: 999,
                                  padding: '2px 7px',
                                }}
                              >
                                ⚠ outlier
                              </span>
                            )}
                          </div>
                          <div
                            className="font-mono"
                            style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}
                          >
                            {fmtRowTime(r.ts)}
                          </div>
                          {r.kit && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                              {r.kit}
                            </div>
                          )}
                          {r.outlier_reason && (
                            <div
                              style={{
                                fontSize: 13,
                                color: 'var(--alert)',
                                marginTop: 4,
                                lineHeight: 1.4,
                              }}
                            >
                              {outlierMessage(r, tank)}
                            </div>
                          )}
                          {r.note && (
                            <div
                              style={{
                                fontSize: 13,
                                color: 'var(--text-muted)',
                                marginTop: 4,
                                lineHeight: 1.4,
                              }}
                            >
                              {r.note}
                            </div>
                          )}
                        </div>
                        {canDelete && (
                          <button
                            type="button"
                            className="ph-del"
                            onClick={() => deleteRow(r)}
                            disabled={deleting === r.id}
                            aria-label="Delete reading"
                            style={{ minWidth: 44, minHeight: 44 }}
                          >
                            {deleting === r.id ? '…' : '×'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
